<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><font-awesome-icon icon="file-invoice-dollar" /> Yêu cầu thay đổi thông tin hoá đơn</h1>
      </div>
    </div>
    <div class="row">
      <!-- Status tabs -->
      <div class="col-12">
        <ul class="nav nav-tabs">
          <li class="nav-item" v-for="tab in statusTabs" :key="tab.value">
            <a class="nav-link" href="#" :class="{active: filterFormModel.status === tab.value}"
               @click.prevent="changeStatus(tab.value)">{{ tab.label }}</a>
          </li>
        </ul>
      </div>

      <div class="col-12">
        <div class="tile">
          <div class="form-inline mb-2">
            <input v-model="filterFormModel.q" class="form-control mr-1 mb-1" style="min-width:240px"
                   placeholder="Tìm theo MST / tên công ty / địa chỉ / email" @keyup.enter="load" />
            <client-select class="mr-1 mb-1" :model="filterFormModel" attribute="client" @change="load" />
            <button @click="load" class="btn btn-primary mr-1 mb-1">Tìm</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>

          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Khách hàng</th>
                  <th>Người gửi</th>
                  <th>Ngày gửi</th>
                  <th>Trạng thái</th>
                  <th class="text-right">Hành động</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!items.length">
                  <td colspan="6" class="text-center text-muted py-3">Không có yêu cầu nào</td>
                </tr>
                <tr v-for="(item, idx) in items" :key="item._id">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ item.client ? (item.client.domain || item.client.companyName || item.client.name) : '-' }}</td>
                  <td>{{ item.requestedBy ? item.requestedBy.name : '-' }}</td>
                  <td>{{ formatTime(item.createTime) }}</td>
                  <td><span class="badge" :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span></td>
                  <td class="text-right">
                    <button class="btn btn-sm btn-outline-primary mr-1" @click="openDetail(item)">Chi tiết</button>
                    <template v-if="item.status === 'pending' && canProcess">
                      <button class="btn btn-sm btn-success mr-1" @click="approve(item)">Duyệt</button>
                      <button class="btn btn-sm btn-danger" @click="openReject(item)">Từ chối</button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal chi tiết: diff before -> payload -->
    <b-modal v-model="detailVisible" title="Chi tiết yêu cầu thay đổi hoá đơn" hide-footer centered size="lg">
      <div v-if="detail && detail.reason" class="alert alert-secondary">
        <b>Lý do khách yêu cầu:</b> {{ detail.reason }}
      </div>
      <table class="table table-bordered" v-if="detail">
        <thead><tr><th>Trường</th><th>Hiện tại</th><th>Đề xuất mới</th></tr></thead>
        <tbody>
          <tr v-for="f in diffRows(detail)" :key="f.key" :class="{ 'table-warning': f.changed }">
            <td>{{ f.label }}</td>
            <td>{{ f.before }}</td>
            <td>{{ f.after }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="detail && detail.status === 'rejected' && detail.rejectReason" class="alert alert-danger">
        Lý do từ chối: {{ detail.rejectReason }}
      </div>
      <div class="text-right mt-2" v-if="detail && detail.status === 'pending' && canProcess">
        <button class="btn btn-success mr-2" @click="approve(detail)">Duyệt</button>
        <button class="btn btn-danger" @click="openReject(detail)">Từ chối</button>
      </div>
    </b-modal>

    <!-- Modal từ chối: nhập lý do -->
    <b-modal v-model="rejectVisible" title="Từ chối yêu cầu" hide-footer centered>
      <div class="form-group">
        <label>Lý do từ chối <span class="text-danger">*</span></label>
        <textarea class="form-control" rows="3" v-model="rejectReason" maxlength="500"
                  placeholder="Nhập lý do để khách hàng biết và chỉnh sửa lại"></textarea>
      </div>
      <div class="text-right">
        <button class="btn btn-secondary mr-2" @click="rejectVisible = false">Huỷ</button>
        <button class="btn btn-danger" :disabled="acting || !rejectReason.trim()" @click="doReject">Xác nhận từ chối</button>
      </div>
    </b-modal>
  </main>
</template>

<script>
import moment from 'moment';
import List from '@/mixins/List';
import ClientSelect from '@/components/ClientSelect.vue';

const FIELDS = [
  { key: 'companyTaxCode', label: 'Mã số thuế' },
  { key: 'companyName', label: 'Tên công ty' },
  { key: 'companyAddress', label: 'Địa chỉ công ty' },
  { key: 'invoiceEmails', label: 'Email nhận hoá đơn' },
  { key: 'invoiceEnabled', label: 'Xuất hoá đơn VAT' },
];

export default {
  mixins: [List],
  components: { ClientSelect },
  data() {
    return {
      service: '/invoice-change-request',
      autoLoad: true,
      defaultFilterFormModel: { page: 1, pageSize: 50, status: 'pending', client: null, q: null },
      statusTabs: [
        { value: 'pending', label: 'Chờ duyệt' },
        { value: 'approved', label: 'Đã duyệt' },
        { value: 'rejected', label: 'Đã từ chối' },
      ],
      detailVisible: false,
      detail: null,
      rejectVisible: false,
      rejectReason: '',
      rejectTarget: null,
      acting: false,
    };
  },
  computed: {
    canProcess() {
      return ['owner', 'admin', 'account', 'accountant'].includes(this.$store.state.user.role);
    },
  },
  methods: {
    changeStatus(status) {
      this.filterFormModel.status = status;
      this.load();
    },
    formatTime(t) {
      return t ? moment(t).format('DD/MM/YYYY HH:mm') : '-';
    },
    statusLabel(s) {
      return { pending: 'Chờ duyệt', approved: 'Đã duyệt', rejected: 'Đã từ chối', cancelled: 'Đã huỷ' }[s] || s;
    },
    statusClass(s) {
      return { pending: 'badge-warning', approved: 'badge-success', rejected: 'badge-danger', cancelled: 'badge-secondary' }[s] || 'badge-secondary';
    },
    fmtVal(key, val) {
      if (key === 'invoiceEmails') return (val && val.length) ? val.join(', ') : 'Chưa có';
      if (key === 'invoiceEnabled') return val ? 'Có' : 'Không';
      return val || 'Chưa có';
    },
    diffRows(item) {
      const before = item.before || {};
      const after = item.payload || {};
      return FIELDS.map(f => {
        const b = this.fmtVal(f.key, before[f.key]);
        const a = this.fmtVal(f.key, after[f.key]);
        return { key: f.key, label: f.label, before: b, after: a, changed: b !== a };
      });
    },
    openDetail(item) {
      this.detail = item;
      this.detailVisible = true;
    },
    approve(item) {
      this.$swal({
        title: 'Duyệt yêu cầu?',
        text: 'Thông tin hoá đơn mới sẽ được áp dụng ngay cho khách hàng.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Duyệt',
        cancelButtonText: 'Huỷ',
      }).then(result => {
        if (!result.value) return;
        const loader = this.$loading.show();
        this.$service.post(`${this.service}/${item._id}/approve`).then(() => {
          this.detailVisible = false;
          this.$swal({ title: 'Đã duyệt', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
          this.load();
        }).catch(error => {
          const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
          this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
        }).finally(() => loader.hide());
      });
    },
    openReject(item) {
      this.rejectTarget = item;
      this.rejectReason = '';
      this.detailVisible = false;
      this.rejectVisible = true;
    },
    doReject() {
      this.acting = true;
      this.$service.post(`${this.service}/${this.rejectTarget._id}/reject`, { reason: this.rejectReason }).then(() => {
        this.rejectVisible = false;
        this.$swal({ title: 'Đã từ chối', icon: 'success', toast: true, position: 'top-end', timer: 3000 });
        this.load();
      }).catch(error => {
        const msg = error.response && error.response.data && (error.response.data.message || error.response.data.msg);
        this.$swal({ text: msg || 'Có lỗi xảy ra', icon: 'error' });
      }).finally(() => {
        this.acting = false;
      });
    },
  },
};
</script>
